import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { Subject, takeUntil, auditTime, filter } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'app-product-score-config',
  imports: [CommonModule, FormsModule, MatIconModule, MatSlideToggleModule],
  standalone: true,
  templateUrl: './product-score-config.component.html',
  styleUrl: './product-score-config.component.scss'
})
export class ProductScoreConfigComponent implements OnInit, OnDestroy {
  @Input() data!: any; // Config passed from parent
  applyConfig = true;
  // Read-only from config
  currentMarketScore = 0;
  currentFinancialScore = 0;
  availableScenarios: number[] = [];

  sliderMin = 0;
  sliderMax = 10;
  sliderStep = 0.1;
  toleranceMax = 2;

  // NEW: per-score mins (exposed for HTML bindings)
  marketSliderMin = 0;       // = Product Fit for Market Score
  financialSliderMin = 0;    // = Financial & Strategic Significance Score

  // User-editable
  marketScore = 0;
  financialScore = 0;
  scenarios: number | null = 1;
  tolerance = 0;

  filters: any = {};
  private destroy$ = new Subject<void>();
  email: string='';

  constructor(
    private configService: ConfigService,
    private globalService: GlobalService,
    private keycloakService: KeycloakService,
  ) { }

  private readMetricNum(options: any[], label: string): number | undefined {
    if (!Array.isArray(options)) return undefined;

    for (const item of options) {
      // Match the label (case-insensitive, trimmed)
      if (String(item?.name).trim().toLowerCase() === label.trim().toLowerCase()) {
        const n = Number(item?.score);
        return Number.isFinite(n) ? n : undefined;
      }

      // Recurse into children
      if (item?.children?.length) {
        const childResult = this.readMetricNum(item.children, label);
        if (childResult !== undefined) return childResult;
      }
    }

    return undefined;
  }


  ngOnInit(): void {
    this.email =
      this.keycloakService.getKeycloakInstance().idTokenParsed?.['email'];
    if (this.data) {
      // Fallbacks (until first API response)
      this.currentMarketScore = this.data.currentScores?.market ?? 0;
      this.currentFinancialScore = this.data.currentScores?.financial ?? 0;

      // initial values
      this.marketScore = this.data.initialValues?.marketTarget ?? 0;
      this.financialScore = this.data.initialValues?.financialTarget ?? 0;
      this.tolerance = this.data.initialValues?.tolerance ?? 0;

      // generation settings
      this.availableScenarios = this.data.generationSettings?.availableScenarios ?? [];

      // slider config
      this.sliderMin = this.data.sliderConfig?.min ?? 0;
      this.sliderMax = this.data.sliderConfig?.max ?? 10;
      this.sliderStep = this.data.sliderConfig?.step ?? 0.1;
      this.toleranceMax = this.data.sliderConfig?.toleranceMax ?? 2;

      this.globalService.reset$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          // Reset the visible sliders immediately so the UI responds even
          // when no filter change triggers a fresh API response.
          this.resetEditableScoresToCurrentValues();
        });

      // Subscribe to global filters, sync URL, and apply dynamic values
      this.globalService.filters$
        .pipe(
          // short delay to collect rapid emissions
          auditTime(1000),
          filter((filters) => filters !== null && filters !== undefined),
          takeUntil(this.destroy$)
        )
        .subscribe((filters) => {
          this.filters = { ...filters.filter };
          const params = {
            segment: this.filters.cleaned_vertical[0],
            platform: this.filters.final_em[0],
            target_x: this.marketScore,
            target_y: this.financialScore,
            num_scenarios: this.scenarios,
            tolerance: this.tolerance,
            userName: this.email
          };
          this.configService
            .customApi(this.data.apiUrl, params, this.data)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => this.applyDynamicResponse(res),
              error: (err) => console.error('Error syncing filters to URL', err),
            });
        });
    }
  }

  generateScenarios() {
    const payload = {
      segment: this.filters.cleaned_vertical[0],
      platform: this.filters.final_em[0],
      target_x: this.marketScore,
      target_y: this.financialScore,
      num_scenarios: this.scenarios,
      tolerance: this.tolerance,
      applyConfig: this.applyConfig,
      userName: this.email
    };
    if (this.applyConfig) {
      const savedConfig = localStorage.getItem('appConfig');
      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          Object.assign(payload, parsedConfig);
        } catch (e) {
          console.error('Invalid JSON in appConfig:', e);
        }
      }
    }
    this.configService
      .customApi(this.data.drilldownUrl, payload, this.data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.globalService.generateScenarios(payload); // notify listeners
        },
        error: (err) => console.error('Error generating scenarios', err),
      });
  }

  private applyDynamicResponse(res: any): void {
    const options: any[] = res.responseData[0]?.drillDownTree ?? [];
    // Scores → also define per-slider mins
    const productFit = this.readMetricNum(options, 'Product Fit for Market Score');
    const finStrategic = this.readMetricNum(options, 'Financial & Strategic Significance Score');

    if (productFit !== undefined) {
      this.currentMarketScore = productFit;
      this.marketSliderMin = productFit;          // MIN for market slider
    }
    if (finStrategic !== undefined) {
      this.currentFinancialScore = finStrategic;
      this.financialSliderMin = finStrategic;     // MIN for financial slider
    }

    // Keep the editable targets aligned with the latest platform values.
    if (productFit !== undefined) {
      this.marketScore = productFit;
    }
    if (finStrategic !== undefined) {
      this.financialScore = finStrategic;
    }

    // Optional: global slider config from response if backend emits such rows
    const sliderMinFromRes = this.readMetricNum(options, 'Slider Min');
    const sliderMaxFromRes = this.readMetricNum(options, 'Slider Max');
    const sliderStepFromRes = this.readMetricNum(options, 'Slider Step');
    const toleranceMaxFromRes = this.readMetricNum(options, 'Tolerance Max');

    if (sliderMinFromRes !== undefined) this.sliderMin = sliderMinFromRes;
    if (sliderMaxFromRes !== undefined) this.sliderMax = sliderMaxFromRes;
    if (sliderStepFromRes !== undefined) this.sliderStep = sliderStepFromRes;
    if (toleranceMaxFromRes !== undefined) this.toleranceMax = toleranceMaxFromRes;

    // Clamp editable values using their OWN mins and shared max
    const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);
    this.marketScore = clamp(this.marketScore, this.marketSliderMin, this.sliderMax);
    this.financialScore = clamp(this.financialScore, this.financialSliderMin, this.sliderMax);
  }

  private resetEditableScoresToCurrentValues(): void {
    this.marketScore = this.currentMarketScore;
    this.financialScore = this.currentFinancialScore;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
